import Icon from '@atoms/Icon/Icon';
import Select from '@atoms/Select/Select';
import Text from '@atoms/Text/Text';
import VariantTypeEnum from '@models/enums/VariantTypeEnum';
import { IPhoneNumberInput } from '@models/IPhoneNumberInput';
import classNames from 'classnames';
import {
  DownshiftState,
  UseSelectState,
  UseSelectStateChangeOptions,
} from 'downshift';
import { useFormContext } from 'react-hook-form';

// Overwrite default state reducer
// Replaces selected label with code instead of full label
// e.g United Kingdom (+44) becomes +44 when selected
const stateReducer = (
  _: DownshiftState<any>,
  actionAndChanges: UseSelectStateChangeOptions<any>
): Partial<UseSelectState<any>> => {
  const { changes } = actionAndChanges;
  const changedSelectedId = changes.selectedItem?.id;

  if (changedSelectedId) {
    return {
      ...changes,
      selectedItem: {
        label: changedSelectedId,
        id: changedSelectedId,
      },
    };
  }
  return changes;
};

const PhoneNumberInput = ({
  phoneNumber,
  dialCode,
  requiredValidationMessage,
}: IPhoneNumberInput) => {
  const { register, setValue, formState } = useFormContext();
  const error =
    formState.errors[phoneNumber.id] || formState.errors[dialCode.id];
  const errorMessage = typeof error?.message === 'string' ? error.message : '';

  return (
    <div className="">
      <Text
        as="label"
        variant="label-12"
        htmlFor={phoneNumber.id}
        className="tw-text-grey-70"
      >
        {phoneNumber.label}
        {requiredValidationMessage ? ' *' : ''}
      </Text>
      <div
        className={classNames(
          'tw-relative tw-flex tw-rounded tw-border',
          error ? 'tw-border-red' : 'tw-border-grey-50'
        )}
      >
        <div className="tw-border-r-1 tw-border-grey-50">
          <Select
            containerClassName="!tw-min-w-0 tw-whitespace-nowrap"
            dropdownClassName="!tw-w-auto"
            name={dialCode.id}
            placeholder={dialCode.placeholder}
            label={dialCode.label}
            options={dialCode.options}
            register={register}
            setValue={setValue}
            displayLabel={false}
            required={!!requiredValidationMessage}
            requiredErrorMessage={requiredValidationMessage}
            variant={VariantTypeEnum.Secondary}
            defaultValue={dialCode.preSelectedValue}
            selectStateReducer={stateReducer}
          />
        </div>
        <input
          id={phoneNumber.id}
          type="tel"
          placeholder={phoneNumber.placeholder}
          {...register(phoneNumber.id, {
            required: requiredValidationMessage,
          })}
          className={classNames(
            'select-text tw-h-xl tw-w-full tw-flex-grow tw-bg-transparent tw-px-xs tw-py-xxs tw-text-sm placeholder:tw-text-grey-70 focus-within:tw-outline-2 focus-within:tw-outline-offset-0',
            {
              ' focus-within:tw-outline-black': !error,
              ' tw-pr-lg focus-within:tw-outline-red': error,
            }
          )}
        />
        {errorMessage && (
          <Icon
            name="notification"
            className="tw-absolute tw-right-xs tw-top-xs tw-text-red"
          />
        )}
      </div>
      {errorMessage && (
        <Text variant="label-12" className="tw-text-red" role="alert">
          {errorMessage}
        </Text>
      )}
    </div>
  );
};

export default PhoneNumberInput;
